import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  DialogFooter,
  showNotification,
} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';

import {isNil, isNotNil, last, times, unnest} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  FINANCING_ENDING_BULK_OPERATION_ITEM_STATE,
  FINANCING_ENDING_EMAIL_OUTCOME,
  financingEndingApi,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {downloadBlobFile, parseDate, useLatest, useOnMount} from 'shared/web';

import {FINANCING_ENDING_DELIVERY_POLLING_INTERVAL_MS} from '../../constants/FINANCING_ENDING_DELIVERY_POLLING_INTERVAL_MS';
import {FINANCING_ENDING_OPERATION_ITEMS_PAGE_SIZE} from '../../constants/FINANCING_ENDING_OPERATION_ITEMS_PAGE_SIZE';
import {FINANCING_ENDING_POLLING_INTERVAL_MS} from '../../constants/FINANCING_ENDING_POLLING_INTERVAL_MS';
import {FINANCING_ENDING_RUNNING_OPERATION_STATES} from '../../constants/FINANCING_ENDING_RUNNING_OPERATION_STATES';
import {buildFinancingEndingOperationCsv} from '../../utils/buildFinancingEndingOperationCsv';
import {getFinancingEndingOperationItemRows} from '../../utils/getFinancingEndingOperationItemRows';
import {FinancingEndingDeliveryDeadlineTimer} from '../FinancingEndingDeliveryDeadlineTimer/FinancingEndingDeliveryDeadlineTimer';
import {FinancingEndingOperationItemsPageSubscription} from '../FinancingEndingOperationItemsPageSubscription/FinancingEndingOperationItemsPageSubscription';
import {FinancingEndingOperationItemTable} from '../FinancingEndingOperationItemTable/FinancingEndingOperationItemTable';
import {FinancingEndingOperationVerdict} from '../FinancingEndingOperationVerdict/FinancingEndingOperationVerdict';
import {FinancingEndingQueryErrorReport} from '../FinancingEndingQueryErrorReport/FinancingEndingQueryErrorReport';

export type FinancingEndingOperationDialogProps = {
  bulkOperationId: string;
  onRerunAccepted: (bulkOperationId: string) => void;
  onUnmount?: () => void;
};

const UNFINISHED_ITEM_STATES: readonly string[] = [
  FINANCING_ENDING_BULK_OPERATION_ITEM_STATE.PENDING,
  FINANCING_ENDING_BULK_OPERATION_ITEM_STATE.IN_PROGRESS,
];

const PENDING_EMAIL_OUTCOMES: readonly string[] = [
  FINANCING_ENDING_EMAIL_OUTCOME.PENDING,
  FINANCING_ENDING_EMAIL_OUTCOME.CREATED,
];

const CSV_FILE_NAME_PREFIX = 'financing-ending-';
const CSV_MIME_TYPE = 'text/csv;charset=utf-8';

type ItemsCacheState = Parameters<
  ReturnType<typeof financingEndingApi.endpoints.listFinancingEndingBulkOperationItems.select>
>[0];

/**
 * E6 counts, the E7 items page by page and "Retry all failed" (E8). Retry exists only for the whole
 * operation and only while it has failed items; skipped items need fixed data and a new sending.
 * While the operation runs, E6 and every loaded E7 page are polled, one request per page; the items
 * stop one poll after the operation, so the last item states are shown. After that, E6 and the loaded
 * pages are polled more slowly while the operation counts or a loaded item has a pending e-mail
 * outcome, until the backend settles it at the latest one hour after the finish. The verdict waits for the first items page, as it counts the
 * e-mail outcomes of the loaded items when the operation has no counts of its own. The CSV export
 * holds the loaded items only.
 */
export function FinancingEndingOperationDialog(props: FinancingEndingOperationDialogProps) {
  const formatDateTime = useDateTimeFormatter();
  const onUnmountRef = useLatest(props.onUnmount);
  const isMountedRef = useRef(false);

  // React StrictMode replays the mount in the same commit, so `onUnmount` runs only when no replay
  // followed the cleanup. A location change drops the dialog without a close, so the unmount is the
  // only signal that both cases send.
  useOnMount(
    () => {
      isMountedRef.current = true;
    },
    () => {
      isMountedRef.current = false;
      void Promise.resolve().then(() => {
        if (!isMountedRef.current) {
          onUnmountRef.current?.();
        }
      });
    }
  );
  const operationArg = {bulkOperationId: props.bulkOperationId};
  const firstItemsPageArg = {
    bulkOperationId: props.bulkOperationId,
    offset: 0,
    limit: FINANCING_ENDING_OPERATION_ITEMS_PAGE_SIZE,
  };
  const {data: polledOperation} =
    financingEndingApi.endpoints.getFinancingEndingBulkOperation.useQueryState(operationArg);
  const {data: polledFirstItemsPage} =
    financingEndingApi.endpoints.listFinancingEndingBulkOperationItems.useQueryState(
      firstItemsPageArg
    );
  const [nextItemsPageCount, setNextItemsPageCount] = useState(0);
  const nextItemsPageArgs = times(
    (pageIndex) => ({
      bulkOperationId: props.bulkOperationId,
      offset: (pageIndex + 1) * FINANCING_ENDING_OPERATION_ITEMS_PAGE_SIZE,
      limit: FINANCING_ENDING_OPERATION_ITEMS_PAGE_SIZE,
    }),
    nextItemsPageCount
  );
  const nextItemsPages = useSelector(
    (state: ItemsCacheState) =>
      nextItemsPageArgs.map(
        (pageArg) =>
          financingEndingApi.endpoints.listFinancingEndingBulkOperationItems.select(pageArg)(state)
            .data ?? []
      ),
    shallowEqual
  );
  const isOperationPolled =
    isNotNil(polledOperation) &&
    FINANCING_ENDING_RUNNING_OPERATION_STATES.includes(polledOperation.state);
  const polledItems = [...(polledFirstItemsPage ?? []), ...unnest(nextItemsPages)];
  const areItemsPolled =
    isOperationPolled || polledItems.some((item) => UNFINISHED_ITEM_STATES.includes(item.state));
  const finishedAt = polledOperation?.finishedAt;
  const [finishedAtPastDeadline, setFinishedAtPastDeadline] = useState<string | null>(null);
  const hasPendingEmailCount = (polledOperation?.emailOutcomeCounts?.pending ?? 0) > 0;
  const isDeliveryPolled =
    isNotNil(finishedAt) &&
    finishedAtPastDeadline !== finishedAt &&
    (hasPendingEmailCount ||
      polledItems.some(
        (item) => isNotNil(item.emailOutcome) && PENDING_EMAIL_OUTCOMES.includes(item.emailOutcome)
      ));
  const itemsPollingInterval = match({areItemsPolled, isDeliveryPolled})
    .with({areItemsPolled: true}, () => FINANCING_ENDING_POLLING_INTERVAL_MS)
    .with({isDeliveryPolled: true}, () => FINANCING_ENDING_DELIVERY_POLLING_INTERVAL_MS)
    .otherwise(() => 0);
  const operationPollingInterval = match({isOperationPolled, isDeliveryPolled})
    .with({isOperationPolled: true}, () => FINANCING_ENDING_POLLING_INTERVAL_MS)
    .with({isDeliveryPolled: true}, () => FINANCING_ENDING_DELIVERY_POLLING_INTERVAL_MS)
    .otherwise(() => 0);

  const {
    data: operation,
    isLoading,
    isError,
  } = financingEndingApi.useGetFinancingEndingBulkOperationQuery(operationArg, {
    pollingInterval: operationPollingInterval,
  });
  const {data: firstItemsPage, error: firstItemsPageError} =
    financingEndingApi.useListFinancingEndingBulkOperationItemsQuery(firstItemsPageArg, {
      pollingInterval: itemsPollingInterval,
    });
  const [listNextItems, {isFetching: isFetchingNextItems}] =
    financingEndingApi.useLazyListFinancingEndingBulkOperationItemsQuery();
  const [rerunFailed, {isLoading: isRerunning}] =
    financingEndingApi.useRerunFailedFinancingEndingBulkOperationMutation();
  const items = [...(firstItemsPage ?? []), ...unnest(nextItemsPages)];
  const itemRows = getFinancingEndingOperationItemRows(items);
  const lastItemsPage = last(nextItemsPages) ?? firstItemsPage ?? [];
  const hasMoreItems = lastItemsPage.length === FINANCING_ENDING_OPERATION_ITEMS_PAGE_SIZE;

  const loadNextItems = () =>
    listNextItems({
      bulkOperationId: props.bulkOperationId,
      offset: (nextItemsPageCount + 1) * FINANCING_ENDING_OPERATION_ITEMS_PAGE_SIZE,
      limit: FINANCING_ENDING_OPERATION_ITEMS_PAGE_SIZE,
    })
      .unwrap()
      .then(() => setNextItemsPageCount(nextItemsPageCount + 1))
      .catch(handleApiError);

  const handleExportCsv = () =>
    downloadBlobFile(
      new Blob(
        [
          buildFinancingEndingOperationCsv(itemRows, (processedAt) =>
            formatDateTime('dateTimeShort', parseDate(processedAt))
          ),
        ],
        {type: CSV_MIME_TYPE}
      ),
      {fileName: `${CSV_FILE_NAME_PREFIX}${props.bulkOperationId}`, fileType: 'csv'}
    );

  const handleRerunFailed = () =>
    rerunFailed({bulkOperationId: props.bulkOperationId})
      .unwrap()
      .then((rerun) => {
        if (isNil(rerun)) {
          showNotification.info(
            i18n.t('entity.businessCase.financingEnding.operation.nothingToRetry')
          );
          return;
        }

        props.onRerunAccepted(rerun.bulkOperationId);
        closeCurrentDialog();
      })
      .catch(handleApiError);

  return (
    <>
      <Show when={isNotNilOrEmpty(finishedAt) ? finishedAt : null}>
        {(finishedAt) => (
          <FinancingEndingDeliveryDeadlineTimer
            key={finishedAt}
            finishedAt={finishedAt}
            onDeadlinePassed={() => setFinishedAtPastDeadline(finishedAt)}
          />
        )}
      </Show>
      {nextItemsPageArgs.map((pageArg) => (
        <FinancingEndingOperationItemsPageSubscription
          key={pageArg.offset}
          pageArg={pageArg}
          pollingInterval={itemsPollingInterval}
        />
      ))}
      <DataStatus
        isLoading={isLoading || (isNil(firstItemsPage) && isNil(firstItemsPageError))}
        isError={isError}
        data={operation}
      >
        {({data: loadedOperation}) => (
          <VStack
            spacing={4}
            data-testid={testIds.businessCase.financingEnding('operationDialogContent')}
          >
            <FinancingEndingOperationVerdict operation={loadedOperation} items={items} />
            <Show when={firstItemsPageError}>
              {(itemsError) => <FinancingEndingQueryErrorReport error={itemsError} />}
            </Show>
            <Show when={itemRows.length > 0}>
              <FinancingEndingOperationItemTable rows={itemRows} />
            </Show>
            <Show when={hasMoreItems}>
              <Button
                variant="link"
                title={i18n.t('entity.businessCase.financingEnding.operation.loadMore')}
                isLoading={isFetchingNextItems}
                isDisabled={isFetchingNextItems}
                onClick={loadNextItems}
                data-testid={testIds.businessCase.financingEnding('operationLoadMore')}
              />
            </Show>
            <DialogFooter>
              <HStack justify="space-between" align="center">
                <Button
                  variant="secondary"
                  title={i18n.t('page.datagrid.labels.exportCSV')}
                  isDisabled={itemRows.length === 0}
                  onClick={handleExportCsv}
                  data-testid={testIds.businessCase.financingEnding('operationExportCsv')}
                />
                <ButtonGroup align="right">
                  <Button
                    variant="secondary"
                    title={i18n.t('general.actions.close')}
                    onClick={closeCurrentDialog}
                    data-testid={testIds.businessCase.financingEnding('operationClose')}
                  />
                  <Show when={loadedOperation.failedCount > 0}>
                    <Button
                      variant="primary"
                      title={i18n.t(
                        'entity.businessCase.financingEnding.operation.retryFailedItems'
                      )}
                      isLoading={isRerunning}
                      isDisabled={isRerunning}
                      onClick={handleRerunFailed}
                      data-testid={testIds.businessCase.financingEnding('operationRetryAllFailed')}
                    />
                  </Show>
                </ButtonGroup>
              </HStack>
            </DialogFooter>
          </VStack>
        )}
      </DataStatus>
    </>
  );
}
